import React from 'react'
import classNames from 'classnames'
import conf, { isGlobal } from 'conf'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { timeFormat, formatBytes } from 'utils'
import { SERVICE_LOGO_IMAGE, SERVICE_COPYRIGHT, SETTING_SERVICE_LOGO_IMAGE } from 'service'
import PLAN_TYPE, { QUOTA_MODE } from 'types/utils'
import aboutLogoIcon1 from '@/assets/images/logo/icon-about-logo.svg'
import styles from '../style.module.scss'

const AboutRender = () => {
  const { t } = useTranslation()
  const user = useSelector((state) => state.user)
  const { paidLicense } = user

  const formatDateRange = () => {
    if (!paidLicense?.started_at || !paidLicense?.expires_at) {
      return null
    }

    // 'll' = moment 로케일 정의 날짜 포맷(축약 월). 'MMM D, YYYY' 처럼 영어 어순을 박아두면
    // 월 이름만 번역돼 "8월 24, 2026"(ko) / "8月 24, 2026"(ja) 로 깨진다 — 어순 자체가
    // 로케일 소관이므로 토큰에 맡긴다. ko "2026년 8월 24일" · ja "2026年8月24日" ·
    // en "Aug 24, 2026"(기존과 동일). 구분자도 로케일별로 달라 문구 키로 뺐다(ko/ja 는 ~).
    const startDate = timeFormat(paidLicense.started_at, 'll')
    const endDate = timeFormat(paidLicense.expires_at, 'll')
    return t('appConfig.settings.licensePeriod', { start: startDate, end: endDate })
  }

  const dateRange = formatDateRange()

  const getLicenseLabel = () => {
    if (!paidLicense) return null

    const planType = paidLicense.plan_type
    if (planType === PLAN_TYPE.TRIAL) {
      return t('appConfig.settings.trialLicense')
    }
    // 등급은 여기 붙이지 않는다 — 축과 묶어 아래 getPlanSummary() 한 줄로 내려간다
    // (2026-08-27 요청: "유료 라이센스" / "레코드 기준 - 프로페셔널" 2줄 구성).
    return t('appConfig.settings.paidLicense')
  }

  // 축(과금 기준) + 등급을 라이센스 종류 바로 아래 한 줄로 — "레코드 기준 - 프로페셔널".
  // 이전에는 축이 상세 표의 "요금제 종류" 행에, 등급이 라이센스 줄 뒤(" · 프로페셔널")에
  // 흩어져 있어 계약 형태가 한눈에 안 들어왔다. 등급명은 음차 표기로 로컬라이즈
  // (ko 베이직 / ja ベーシック — 상품명 발음 보존, 2026-08-24 결정).
  // 한국향은 축·등급 개념이 없으므로 렌더하지 않는다. 글로벌향·PAID 는 NULL=Basic 취급.
  const getPlanSummary = () => {
    if (!paidLicense || !isGlobal() || !paidLicense.quota_mode) return null

    const quotaModeLabel = t(
      paidLicense.quota_mode === QUOTA_MODE.USER_LIMIT
        ? 'appConfig.settings.planQuotaModeUser'
        : 'appConfig.settings.planQuotaModeResource',
    )

    // 체험판은 등급 구분이 없다(PRO 동급) — 축만 보여준다.
    if (paidLicense.plan_type === PLAN_TYPE.TRIAL) return quotaModeLabel

    const tierLabel =
      paidLicense.plan_tier === 'PROFESSIONAL'
        ? t('appConfig.settings.tierProfessional')
        : t('appConfig.settings.tierBasic')

    return `${quotaModeLabel} - ${tierLabel}`
  }

  // 요금제 상세 행 — "유료 라이센스 · 프로페셔널" 한 줄만으론 계약 내용을 알 수 없어
  // license 응답이 이미 내려주는 값들을 그대로 풀어 보여준다(추가 API 없음, 2026-08-25).
  // 각 행은 해당 필드가 있을 때만 노출 — 플랜 미적용 환경(한국향 등)은 자연히 빈 목록이 되고
  // 그 경우 상세 블록 자체를 렌더하지 않는다.
  const getPlanDetailRows = () => {
    if (!paidLicense || !isGlobal()) return []

    const rows = []

    // 과금 축(요금제 종류)은 여기 두지 않는다 — getPlanSummary() 요약 줄로 올라갔다.
    // 같은 화면에 "레코드 기준"이 두 번 나오지 않게 한다 (2026-08-27).

    // 첨부 파일당 상한 — 등급으로 갈리는 대표 수치(Basic 10MB / PRO·체험판 30MB)
    if (paidLicense.max_file_size_bytes > 0) {
      rows.push({
        key: 'maxFileSize',
        label: t('appConfig.settings.planMaxFileSize'),
        value: formatBytes(paidLicense.max_file_size_bytes, 0),
      })
    }

    // 저장 용량 — 회사 풀(A축 전체·B축 Basic)과 개인당(B축 PRO)은 서버가 상호 배타로 내려준다.
    // 소수 1자리로 표시한다(사이드바 게이지와 동일): 상한은 계약수량×300MiB 라 GiB 로 나누면
    // 소수가 남고(100건=30,000MB=29.3GiB), 0자리로 자르면 "29 GB" 가 되어 계약 수치와
    // 어긋나 보인다. formatBytes 는 parseFloat 를 거쳐 .0 을 자동으로 떼므로 2GB·30MB 처럼
    // 딱 떨어지는 값은 그대로 정수로 나온다.
    const personalQuota = Number(paidLicense.personal_storage_quota_bytes) || 0
    const companyQuota = Number(paidLicense.storage_quota_bytes) || 0
    if (personalQuota > 0) {
      rows.push({
        key: 'personalStorage',
        label: t('appConfig.settings.planPersonalStorage'),
        value: formatBytes(personalQuota, 1),
      })
    } else if (companyQuota > 0) {
      rows.push({
        key: 'companyStorage',
        label: t('appConfig.settings.planCompanyStorage'),
        value: formatBytes(companyQuota, 1),
      })
    }

    return rows
  }

  const licenseLabel = getLicenseLabel()
  const planSummary = getPlanSummary()
  const planDetailRows = getPlanDetailRows()

  return (
    <div className={classNames(styles.aboutContent)}>
      <img src={aboutLogoIcon1} alt={SERVICE_LOGO_IMAGE} className={classNames(styles.logo)} />
      <div className={classNames(styles.versionInfo)}>
        <img src={SETTING_SERVICE_LOGO_IMAGE} alt="logo" className={classNames(styles.logoText)} />
        <span>Version {conf.packageInfo.version}</span>
      </div>
      {paidLicense && licenseLabel && (
        <div className={classNames(styles.paidLicense)}>
          <span>{licenseLabel}</span>
          {planSummary && <span>{planSummary}</span>}
          {dateRange && <span>{dateRange}</span>}
        </div>
      )}
      {planDetailRows.length > 0 && (
        <dl className={classNames(styles.planDetail)}>
          {planDetailRows.map((row) => (
            <div key={row.key} className={classNames(styles.planDetailRow)}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <span>{SERVICE_COPYRIGHT}</span>
    </div>
  )
}

export default AboutRender
